package gr.tech.GRAttestation.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import gr.tech.GRAttestation.components.StorePaginationControls
import gr.tech.GRAttestation.components.layouts.StoreUserPageLayout
import gr.tech.GRAttestation.components.widgets.SuccessCheckmark
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.api.fetchAttestationsWithUserStoreId
import gr.tech.GRAttestation.infrastructure.api.fetchSKUOptions
import gr.tech.GRAttestation.infrastructure.api.fetchStoreByCurrentUser
import gr.tech.GRAttestation.infrastructure.api.saveUserPDFWithUserStoreId
import gr.tech.GRAttestation.infrastructure.session.StoreUserSession
import gr.tech.GRAttestation.models.attestation.AttestationDTO
import gr.tech.GRAttestation.models.skus.SkuPreviewDTO
import gr.tech.GRAttestation.models.stores.StorePreviewDTO
import gr.tech.GRAttestation.models.user.USER_ROLES
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.sections.document.AttestationDocument
import gr.tech.GRAttestation.sections.forms.AttestationForm
import gr.tech.GRAttestation.sections.store.StoreInfoBanner
import gr.tech.GRAttestation.sections.tables.StoreAttestationHistoryTable
import gr.tech.GRAttestation.styles.GenDefaultText
import gr.tech.GRAttestation.styles.defaultText
import gr.tech.GRAttestation.styles.grstyles.DarkGreenHoverButtonStyle
import gr.tech.GRAttestation.styles.grstyles.DarkGreenOutlineHoverButtonStyle
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.styles.noBorder
import gr.tech.GRAttestation.styles.store.*
import gr.tech.GRAttestation.util.GeneratePDFTool
import gr.tech.GRAttestation.util.PDFGenStatus
import gr.tech.GRAttestation.util.formatCurrency
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import gr.tech.GRAttestation.models.attestation.AttestationForm as AttestationFormData

val formContainer = CssStyle {
    base {
        Modifier
            .width(min(900.px, 100.percent - 1.cssRem))
            .noBorder()
            .borderRadius(8.px)
            .padding(16.px)
            .margin(topBottom = 24.px)
            .opacity(1)
    }
}

enum class DASHBOARD_TAB {
    SALES,
    HISTORY
}

@Page
@Composable
fun HomePage() {
    StoreUserPageLayout(
        title = "Dashboard",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role),
    ) {
        HomeScreen()
    }
}

@Composable
fun HomeScreen() {
    val breakpoint = rememberBreakpoint()
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    var activeTab by remember { mutableStateOf(DASHBOARD_TAB.SALES) }

    var store: StorePreviewDTO? by remember { mutableStateOf(null) }
    val skuOptions = remember { mutableStateListOf<SkuPreviewDTO>() }

    var storeLoadError by remember { mutableStateOf("") }
    var skuLoadError by remember { mutableStateOf("") }

    var generationStatus: PDFGenStatus? by remember { mutableStateOf(null) }
    var submittedFormData: AttestationFormData? by remember { mutableStateOf(null) }
    val generator = remember { GeneratePDFTool() }

    val attestations = remember { mutableStateListOf<AttestationDTO>() }
    var attestationsLoading by remember { mutableStateOf(false) }
    var attestationsError by remember { mutableStateOf("") }

    val historyPageSize = 10
    var historyPage by remember { mutableStateOf(1) }
    var historyTotalCount by remember { mutableStateOf(0) }
    val historyTotalPages = (historyTotalCount + historyPageSize - 1) / historyPageSize

    fun loadHistory(page: Int) {
        scope.launch {
            attestationsLoading = true
            attestationsError = ""
            fetchAttestationsWithUserStoreId(
                pageNumber = page,
                pageSize = historyPageSize,
                onResponse = { response ->
                    if (response.success && response.data != null) {
                        attestations.clear()
                        attestations.addAll(response.data.items)
                        historyTotalCount = response.data.totalCount
                    } else {
                        attestationsError = "Failed to load submission history."
                    }
                    attestationsLoading = false
                },
                onError = {
                    attestationsError = it
                    attestationsLoading = false
                }
            )
        }
    }

    // Note - Typical Fetching
    LaunchedEffect(Unit) {
        fetchStoreByCurrentUser(
            onSuccess = { response ->
                if (response.success && response.data != null) {
                    store = response.data
                } else {
                    storeLoadError = response.error?.message ?: "Failed to load store information."
                }
            },
            onError = {
                storeLoadError = "Failed to load store information. Please refresh the page."
            }
        )

        fetchSKUOptions(
            onSuccess = { response ->
                if (response.success && response.data != null) {
                    skuOptions.clear()
                    skuOptions.addAll(response.data)
                } else {
                    skuLoadError = response.error?.message ?: "Failed to load product options."
                }
            },
            onError = {
                skuLoadError = "Failed to load product options. Please refresh the page."
            }
        )

        loadHistory(1)
    }

    // Note - Triggers PDF generation after form is submitted and off-screen doc is rendered
    LaunchedEffect(submittedFormData) {
        submittedFormData?.let { submitData ->
            if (store == null) {
                console.error("PDF generation aborted: store data invalid.")
                generationStatus = PDFGenStatus.FAILED
                return@let
            }
            if (StoreUserSession.getRepresentativeName().isBlank()) {
                console.error("PDF generation aborted: user data invalid.")
                generationStatus = PDFGenStatus.FAILED
                return@let
            }

            generator.genPDF(
                listOfIds = listOf("attestation-form"),
                onGenerationStatus = { status ->
                    when (status) {
                        PDFGenStatus.INSTANCE_READY -> {
                            generator.getBlob(
                                onBlobReady = { blob ->
                                    scope.launch {
                                        val uploadStatus = saveUserPDFWithUserStoreId(
                                            attestation = submitData,
                                            pdfBlob = blob,
                                            repName = StoreUserSession.getRepresentativeName(),
                                            storeName = store!!.storeName
                                        )
                                        generationStatus = if (uploadStatus.success) {
                                            PDFGenStatus.READY_DOWNLOAD
                                        } else {
                                            PDFGenStatus.FAILED
                                        }
                                    }
                                },
                                onError = {
                                    generationStatus = PDFGenStatus.FAILED
                                    console.error("Blob generation failed!")
                                }
                            )
                        }
                        PDFGenStatus.FAILED -> generationStatus = PDFGenStatus.FAILED
                        else -> {}
                    }
                }
            )
        }
    }

    Box(Modifier.fillMaxSize().backgroundColor(Theme.GreenTint.rgb), contentAlignment = Alignment.TopCenter) {
        Column(modifier = formContainer.toModifier()) {
            StoreInfoBanner(store = store)

            // Tab Bar
            Row(modifier = DashboardTabBarStyle.toModifier()) {
                Box(modifier =
                        (if (activeTab == DASHBOARD_TAB.SALES) DashboardTabButtonActiveStyle else DashboardTabButtonInactiveStyle)
                            .toModifier()
                            .onClick { activeTab = DASHBOARD_TAB.SALES }
                ) { SpanText("Sales Report") }

                Box(modifier =
                        (if (activeTab == DASHBOARD_TAB.HISTORY) DashboardTabButtonActiveStyle else DashboardTabButtonInactiveStyle)
                            .toModifier()
                            .onClick { activeTab = DASHBOARD_TAB.HISTORY}
                ) { SpanText("History") }
            }

            // Tab Content
            Column(modifier = DashboardTabCardStyle.toModifier()) {
                when (activeTab) {
                    DASHBOARD_TAB.SALES -> {
                        // Card Header
                        Column(modifier = DashboardTabCardHeaderStyle.toModifier()) {
                            SpanText(
                                text = "Monthly Sales Report",
                                modifier = DashboardTabCardTitleStyle.toModifier()
                            )
                            P(attrs = DashboardTabCardSubtitleStyle.toModifier().toAttrs()) {
                                Text("Report rebate-eligible grow light sales for the reporting period.")
                            }
                        }
                        // Card Body
                        Column(
                            modifier = DashboardTabCardBodyStyle.toModifier(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            when (generationStatus) {
                                null -> {
                                    if (storeLoadError.isNotBlank() || skuLoadError.isNotBlank()) {
                                        P(attrs = GenDefaultText.margin(topBottom = 16.px).toAttrs()) {
                                            Text(storeLoadError.ifBlank { skuLoadError })
                                        }
                                        Button(
                                            attrs = Modifier.classNames("btn")
                                                .then(DarkGreenHoverButtonStyle.toModifier())
                                                .onClick { it.preventDefault(); window.location.reload() }
                                                .toAttrs()
                                        ) { Text("Refresh") }
                                    } else {
                                    AttestationForm(
                                        skuOptions = skuOptions,
                                        breakpoint = breakpoint
                                    ) { submittedForm ->
                                        if (store != null) {
                                            submittedFormData = submittedForm
                                            generationStatus = PDFGenStatus.GENERATING
                                        }
                                    }
                                    }
                                }
                                PDFGenStatus.GENERATING,
                                PDFGenStatus.INSTANCE_READY,
                                PDFGenStatus.BLOB_READY -> {
                                    loadingSpinner(
                                        iconSize = IconSize.XXL,
                                        modifier = Modifier
                                            .margin(topBottom = 48.px)
                                            .scale(1.5)
                                            .color(Theme.DarkGreen.rgb)
                                    )
                                }
                                PDFGenStatus.READY_DOWNLOAD -> {
                                    SuccessCheckmark()

                                    P(attrs = PortalTextStyle.toModifier().toAttrs()) {
                                        Text("Sales Report Submitted")
                                    }

                                    P(attrs = PortalSmallTextStyle.toModifier().classNames("mb-3").toAttrs()) {
                                        Text("Report for ${StoreUserSession.getRepresentativeName()} - ${submittedFormData?.quantity} units, ${formatCurrency(submittedFormData?.incentiveAmount ?: 0)} incentive")
                                    }

                                    Row(
                                        modifier = Modifier.classNames("gap-1"),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Button(
                                            attrs = Modifier.classNames("btn")
                                                .width(150.px)
                                                .then(DarkGreenHoverButtonStyle.toModifier())
                                                .onClick {
                                                    it.preventDefault()
                                                    submittedFormData = null
                                                    generationStatus = null
                                                }
                                                .toAttrs()
                                        ) { Text("New Report") }

                                        Button(
                                            attrs = Modifier.classNames("btn")
                                                .width(150.px)
                                                .then(DarkGreenOutlineHoverButtonStyle.toModifier())
                                                .onClick {
                                                    historyPage = 1
                                                    loadHistory(1)
                                                    activeTab = DASHBOARD_TAB.HISTORY
                                                }
                                                .toAttrs()
                                        ) { Text("View History") }
                                    }
                                }
                                PDFGenStatus.FAILED -> {
                                    P(attrs = GenDefaultText.margin(topBottom = 16.px).toAttrs()) {
                                        Text("Something went wrong. Please try again.")
                                    }

                                    Button(
                                        attrs = Modifier.classNames("btn")
                                            .width(175.px)
                                            .margin(bottom = 32.px, leftRight = autoLength)
                                            .then(DarkGreenHoverButtonStyle.toModifier())
                                            .onClick {
                                                it.preventDefault()
                                                submittedFormData = null
                                                generationStatus = null
                                            }
                                            .toAttrs()
                                    ) { Text("New Report") }
                                }
                            }
                        }
                    }
                    DASHBOARD_TAB.HISTORY -> {
                        // Card Header
                        Row(
                            modifier = DashboardTabCardHeaderStyle.toModifier(),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            SpanText(
                                text = "Submission History",
                                modifier = DashboardTabCardTitleStyle.toModifier().weight(1)
                            )
                            Button(
                                attrs = Modifier.classNames("btn")
                                    .then(DarkGreenHoverButtonStyle.toModifier())
                                    .fontSize(12.px)
                                    .onClick { it.preventDefault(); historyPage = 1; loadHistory(1) }
                                    .toAttrs()
                            ) { Text("Refresh") }
                        }
                        // Card Body
                        Column(
                            modifier = DashboardTabCardBodyStyle.toModifier(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            when {
                                attestationsLoading -> {
                                    loadingSpinner(
                                        iconSize = IconSize.XL,
                                        modifier = Modifier.margin(topBottom = 32.px).color(Theme.DarkGreen.rgb)
                                    )
                                }
                                attestationsError.isNotBlank() -> {
                                    P(attrs = GenDefaultText.margin(topBottom = 16.px).toAttrs()) {
                                        Text(attestationsError)
                                    }
                                    Button(
                                        attrs = Modifier.classNames("btn")
                                            .then(DarkGreenHoverButtonStyle.toModifier())
                                            .onClick { it.preventDefault(); loadHistory(historyPage) }
                                            .toAttrs()
                                    ) { Text("Try Again") }
                                }
                                attestations.isEmpty() -> {
                                    P(attrs = GenDefaultText.margin(topBottom = 32.px).toAttrs()) {
                                        Text("No submissions found.")
                                    }
                                }
                                else -> {
                                    StoreAttestationHistoryTable(
                                        attestations = attestations
                                    )
                                    if (historyTotalPages > 1) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth().margin(top = 16.px),
                                            horizontalArrangement = Arrangement.Center
                                        ) {
                                            StorePaginationControls(
                                                currentPage = historyPage,
                                                totalPages = historyTotalPages,
                                                onPageSelected = { page ->
                                                    historyPage = page
                                                    loadHistory(page)
                                                }
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            P(attrs = PortalSmallTextStyle.toModifier().classNames("my-3", "mx-auto").toAttrs()) {
                Text("Having trouble? Contact us at ")
                A(
                    href = "mailto:retail@greenrebates.com",
                    attrs = Modifier.color(Theme.DarkGreen.rgb).toAttrs()
                ) {
                    Text("retail@greenrebates.com")
                }
                Text(" for assistance.")
            }

            SpanText(
                text = "© 2026, Green Rebates. All Rights Reserved.",
                modifier = Modifier.defaultText().margin(leftRight = autoLength).fontSize(0.8.cssRem)
            )

            Row(
                modifier = Modifier.fillMaxWidth().gap(16.px),
                horizontalArrangement = Arrangement.Center
            ) {
                Link(
                    path = SitePage.PrivacyPolicy.route,
                    modifier = Modifier.defaultText().color(Theme.DarkGreen.rgb).fontSize(0.8.cssRem)
                ) {
                    Text("Privacy Policy")
                }

                Link(
                    path = SitePage.TermsConditions.route,
                    modifier = Modifier.defaultText().color(Theme.DarkGreen.rgb).fontSize(0.8.cssRem)
                ) {
                    Text("Terms & Conditions")
                }
            }
        }
    }

    // Off-screen attestation document — in the DOM for html2pdf to capture, invisible to the user
    if (submittedFormData != null && store != null) {
        Div(
            attrs = Modifier
                .position(Position.Fixed)
                .left((-999999).px)
//                .left((0).px) // Swap with above for testing visually the form.
                .top(0.px)
                .toAttrs{}
        ) {
            AttestationDocument(formData = submittedFormData!!, storeData = store!!)
        }
    }
}
